/** Auto-fallback that pretty-prints the agent's raw output_json.
 *
 *  Always rendered under StageRichBody. Collapsed by default when the
 *  structured renderer has substantive content; opens automatically when
 *  the structured renderer would be blank (Pydantic rejected the shape,
 *  agent renamed a field, salvage parser kept the data but under a key
 *  CMP doesn't recognise, etc.).
 *
 *  Hides internal markers (`_duration_ms`, `_tokens_used`, etc.) so the
 *  reviewer sees the agent's actual output, not CMP plumbing. */
import { Badge } from '@/components/ui/badge';
import { AlertCircle } from 'lucide-react';

const INTERNAL_KEYS = new Set([
  '_duration_ms',
  '_tokens_used',
  '_raw_text',
  '_kaycoo_call_failed',
  '_pydantic_validation_failed',
  '_backfilled_from_narrative',
  '_backfilled_at',
  '_truncated',
  '_error',
  '_manual_ingest',
  '_manual_execution_id',
  '_manual_ingest_note',
]);

interface Props {
  output: Record<string, unknown>;
  /** When true the <details> opens automatically — used when the structured
   *  Rich*Body would render blank so the reviewer always sees something. */
  initiallyOpen: boolean;
}

export function RawOutputFallback({ output, initiallyOpen }: Props) {
  const visible = Object.fromEntries(
    Object.entries(output).filter(([k]) => !INTERNAL_KEYS.has(k)),
  );
  // Nothing the agent itself contributed — don't render the panel.
  if (Object.keys(visible).length === 0) return null;

  const pydanticFailed = Boolean(output._pydantic_validation_failed);
  const backfilled = Boolean(output._backfilled_from_narrative);
  const truncated = Boolean(output._truncated);

  return (
    <details className="text-xs mt-2" open={initiallyOpen}>
      <summary className="cursor-pointer text-muted-foreground hover:text-foreground inline-flex items-center gap-2">
        {initiallyOpen && (
          <AlertCircle className="h-3.5 w-3.5 text-amber-600" aria-hidden="true" />
        )}
        <span>
          {initiallyOpen
            ? "CMP didn't recognise this output shape — showing raw agent response"
            : 'Show raw agent output (JSON)'}
        </span>
        {pydanticFailed && (
          <Badge variant="outline" className="text-[10px] bg-amber-50 border-amber-300 text-amber-800">
            schema rejected
          </Badge>
        )}
        {truncated && (
          <Badge variant="outline" className="text-[10px] bg-amber-50 border-amber-300 text-amber-800">
            truncated
          </Badge>
        )}
        {backfilled && (
          <Badge variant="outline" className="text-[10px] bg-blue-50 border-blue-300 text-blue-800">
            recovered from narrative
          </Badge>
        )}
      </summary>
      <pre className="mt-2 max-h-[600px] overflow-auto rounded border bg-muted/30 p-3 text-xs whitespace-pre-wrap break-words font-mono leading-relaxed">
        {JSON.stringify(visible, null, 2)}
      </pre>
    </details>
  );
}
